type RequestSrcElement = HTMLIFrameElement | HTMLImageElement | HTMLScriptElement;
type RequestSrcElementConstructor = {
  prototype: RequestSrcElement;
};
type UrlRewriter = {
  (rawUrl: string): string;
  (rawUrl: URL): URL;
  (rawUrl: string | URL): string | URL;
};

const META_PIXEL_REQUEST_PATHS = ["/tr", "/privacy_sandbox/pixel/register/trigger"] as const;
const META_PIXEL_REQUEST_SANITIZATION_INSTALLED = "__tapestryMetaAdsRequestSanitizationInstalled";
const META_PIXEL_XML_HTTP_REQUEST_URL = "__tapestryMetaAdsRequestUrl";

type MetaPixelSanitizationWindow = Window & {
  [META_PIXEL_REQUEST_SANITIZATION_INSTALLED]?: boolean;
};
type MetaPixelRequestUrlElement = HTMLFormElement | HTMLImageElement;
type MetaPixelRequestUrlElementConstructor = {
  prototype: MetaPixelRequestUrlElement;
};
type MetaPixelXmlHttpRequest = XMLHttpRequest & {
  [META_PIXEL_XML_HTTP_REQUEST_URL]?: string | URL;
};
type MetaPixelRequestBody = BodyInit | Document | null | undefined;
type MetaPixelXmlHttpRequestBody = Document | XMLHttpRequestBodyInit | null | undefined;
type MetaPixelPageContextParams = {
  has(queryParam: string): boolean;
  set(queryParam: string, value: string): void;
};

const RESTRICTED_META_PIXEL_EVENT_CONTEXT = {
  documentLocation: "https://ads.openai.com/",
  referrer: "",
} as const;
const META_PIXEL_PAGE_CONTEXT_QUERY_PARAM_REPLACEMENTS = [
  ["dl", RESTRICTED_META_PIXEL_EVENT_CONTEXT.documentLocation],
  ["rl", RESTRICTED_META_PIXEL_EVENT_CONTEXT.referrer],
  ["cd[dl]", RESTRICTED_META_PIXEL_EVENT_CONTEXT.documentLocation],
  ["cd[rl]", RESTRICTED_META_PIXEL_EVENT_CONTEXT.referrer],
] as const;
const META_PIXEL_PAGE_CONTEXT_QUERY_PARAM_REPLACEMENT_MAP = new Map<string, string>(
  META_PIXEL_PAGE_CONTEXT_QUERY_PARAM_REPLACEMENTS,
);

function rewriteMetaPixelPageContextParams(params: MetaPixelPageContextParams) {
  let wasRewritten = false;
  for (const [queryParam, value] of META_PIXEL_PAGE_CONTEXT_QUERY_PARAM_REPLACEMENTS) {
    if (params.has(queryParam)) {
      params.set(queryParam, value);
      wasRewritten = true;
    }
  }
  return wasRewritten;
}

function hasMetaPixelRequestPath(url: URL) {
  const pathname = url.pathname.toLowerCase();
  return META_PIXEL_REQUEST_PATHS.some(
    (requestPath) => pathname === requestPath || pathname.startsWith(`${requestPath}/`),
  );
}

function hasMetaPixelRequestHost(url: URL) {
  const hostname = url.hostname.toLowerCase();
  return (
    hostname === "facebook.com" ||
    hostname.endsWith(".facebook.com") ||
    hostname === "instagram.com" ||
    hostname.endsWith(".instagram.com")
  );
}

function isMetaPixelRequestUrl(url: URL) {
  return hasMetaPixelRequestHost(url) && hasMetaPixelRequestPath(url);
}

function getMetaPixelRequestUrl(rawUrl: string | URL) {
  try {
    const url = new URL(rawUrl.toString(), window.location.href);
    return isMetaPixelRequestUrl(url) ? url : null;
  } catch {
    return null;
  }
}

function hasMetaPixelPageContextQueryParamInRawUrl(rawUrl: string) {
  return META_PIXEL_PAGE_CONTEXT_QUERY_PARAM_REPLACEMENTS.some(
    ([queryParam]) =>
      rawUrl.includes(`${queryParam}=`) || rawUrl.includes(`${encodeURIComponent(queryParam)}=`),
  );
}

export function rewriteMetaAdsRequestUrl(rawUrl: string): string;
export function rewriteMetaAdsRequestUrl(rawUrl: URL): URL;
export function rewriteMetaAdsRequestUrl(rawUrl: string | URL): string | URL;
export function rewriteMetaAdsRequestUrl(rawUrl: string | URL) {
  try {
    const rawUrlString = rawUrl.toString();
    if (!hasMetaPixelPageContextQueryParamInRawUrl(rawUrlString)) {
      return rawUrl;
    }

    const url = getMetaPixelRequestUrl(rawUrl);
    if (url == null || !rewriteMetaPixelPageContextParams(url.searchParams)) {
      return rawUrl;
    }

    return rawUrl instanceof URL ? url : url.toString();
  } catch {
    return rawUrl;
  }
}

function rewriteMetaPixelRequestBody(
  body: BodyInit | null | undefined,
): BodyInit | null | undefined;
function rewriteMetaPixelRequestBody(
  body: MetaPixelXmlHttpRequestBody,
): MetaPixelXmlHttpRequestBody;
function rewriteMetaPixelRequestBody(body: MetaPixelRequestBody): MetaPixelRequestBody;
function rewriteMetaPixelRequestBody(body: MetaPixelRequestBody) {
  if (body == null) {
    return body;
  }

  if (typeof body === "string") {
    const searchParams = new URLSearchParams(body);
    return rewriteMetaPixelPageContextParams(searchParams) ? searchParams.toString() : body;
  }

  if (body instanceof URLSearchParams) {
    const rewrittenSearchParams = new URLSearchParams(body);
    return rewriteMetaPixelPageContextParams(rewrittenSearchParams) ? rewrittenSearchParams : body;
  }

  if (body instanceof FormData) {
    const rewrittenFormData = new FormData();
    body.forEach((value, queryParam) => {
      rewrittenFormData.append(queryParam, value);
    });
    return rewriteMetaPixelPageContextParams(rewrittenFormData) ? rewrittenFormData : body;
  }

  return body;
}

function rewriteMetaPixelRequestBodyForUrl(
  rawUrl: string | URL,
  body: BodyInit | null | undefined,
): BodyInit | null | undefined;
function rewriteMetaPixelRequestBodyForUrl(
  rawUrl: string | URL,
  body: MetaPixelXmlHttpRequestBody,
): MetaPixelXmlHttpRequestBody;
function rewriteMetaPixelRequestBodyForUrl(
  rawUrl: string | URL,
  body: MetaPixelRequestBody,
): MetaPixelRequestBody;
function rewriteMetaPixelRequestBodyForUrl(rawUrl: string | URL, body: MetaPixelRequestBody) {
  return getMetaPixelRequestUrl(rawUrl) == null ? body : rewriteMetaPixelRequestBody(body);
}

async function rewriteMetaPixelFetchInput(
  input: RequestInfo | URL,
  hasOverridingBody: boolean,
): Promise<RequestInfo | URL> {
  if (typeof input === "string" || input instanceof URL) {
    return rewriteMetaAdsRequestUrl(input);
  }

  if (typeof Request !== "undefined" && input instanceof Request) {
    const rewrittenUrl = rewriteMetaAdsRequestUrl(input.url);
    const rewrittenRequest = rewrittenUrl === input.url ? input : new Request(rewrittenUrl, input);
    if (hasOverridingBody || input.body == null || getMetaPixelRequestUrl(input.url) == null) {
      return rewrittenRequest;
    }

    try {
      const body = await input.clone().text();
      const rewrittenBody = rewriteMetaPixelRequestBody(body);
      return rewrittenBody === body ? rewrittenRequest : (
          new Request(rewrittenRequest, { body: rewrittenBody })
        );
    } catch {
      return rewrittenRequest;
    }
  }

  return input;
}

function getMetaPixelFetchRequestUrl(input: RequestInfo | URL) {
  if (typeof input === "string" || input instanceof URL) {
    return input;
  }
  if (typeof Request !== "undefined" && input instanceof Request) {
    return input.url;
  }
  return null;
}

function rewriteMetaPixelFetchInit(input: RequestInfo | URL, init: RequestInit | undefined) {
  const requestUrl = getMetaPixelFetchRequestUrl(input);
  if (requestUrl == null || init?.body == null) {
    return init;
  }

  const rewrittenBody = rewriteMetaPixelRequestBodyForUrl(requestUrl, init.body);
  return rewrittenBody === init.body ? init : { ...init, body: rewrittenBody };
}

function installMetaPixelRequestUrlPropertySanitization(
  elementConstructor: MetaPixelRequestUrlElementConstructor,
  propertyName: "action" | "src",
) {
  const propertyDescriptor = Object.getOwnPropertyDescriptor(
    elementConstructor.prototype,
    propertyName,
  );
  if (!propertyDescriptor?.set) {
    return;
  }
  const setProperty = propertyDescriptor.set;

  Object.defineProperty(elementConstructor.prototype, propertyName, {
    ...propertyDescriptor,
    set(this: MetaPixelRequestUrlElement, value: string) {
      setProperty.call(this, rewriteMetaAdsRequestUrl(value));
    },
  });
}

function rewriteMetaPixelFormInputs(form: HTMLFormElement) {
  if (getMetaPixelRequestUrl(form.action) == null) {
    return;
  }

  for (const formElement of Array.from(form.elements)) {
    if (!(formElement instanceof HTMLInputElement)) {
      continue;
    }

    const replacement = META_PIXEL_PAGE_CONTEXT_QUERY_PARAM_REPLACEMENT_MAP.get(formElement.name);
    if (replacement != null) {
      formElement.value = replacement;
    }
  }
}

function installMetaPixelFormRequestSanitization() {
  if (typeof HTMLFormElement === "undefined") {
    return;
  }

  installMetaPixelRequestUrlPropertySanitization(HTMLFormElement, "action");

  const originalSubmit = HTMLFormElement.prototype.submit;
  HTMLFormElement.prototype.submit = function (this: HTMLFormElement) {
    rewriteMetaPixelFormInputs(this);
    return Reflect.apply(originalSubmit, this, []);
  };
}

export function installMetaAdsRequestSanitization() {
  if (typeof window === "undefined") {
    return;
  }

  const metaPixelWindow: MetaPixelSanitizationWindow = window;
  if (metaPixelWindow[META_PIXEL_REQUEST_SANITIZATION_INSTALLED]) {
    return;
  }
  metaPixelWindow[META_PIXEL_REQUEST_SANITIZATION_INSTALLED] = true;

  if (typeof window.fetch === "function") {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const rewrittenInit = rewriteMetaPixelFetchInit(input, init);
      const rewrittenInput = await rewriteMetaPixelFetchInput(input, rewrittenInit?.body != null);
      return originalFetch(rewrittenInput, rewrittenInit);
    };
  }

  if (typeof navigator.sendBeacon === "function") {
    const originalSendBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = (url, data) =>
      originalSendBeacon(
        rewriteMetaAdsRequestUrl(url),
        rewriteMetaPixelRequestBodyForUrl(url, data),
      );
  }

  if (typeof XMLHttpRequest !== "undefined") {
    const originalOpen = XMLHttpRequest.prototype.open;
    const sanitizedOpen: XMLHttpRequest["open"] = function (
      this: MetaPixelXmlHttpRequest,
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      if (getMetaPixelRequestUrl(url) == null) {
        delete this[META_PIXEL_XML_HTTP_REQUEST_URL];
      } else {
        this[META_PIXEL_XML_HTTP_REQUEST_URL] = url;
      }
      return Reflect.apply(originalOpen, this, [method, rewriteMetaAdsRequestUrl(url), ...rest]);
    };
    XMLHttpRequest.prototype.open = sanitizedOpen;

    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (
      this: MetaPixelXmlHttpRequest,
      body?: Document | XMLHttpRequestBodyInit | null,
    ) {
      const requestUrl = this[META_PIXEL_XML_HTTP_REQUEST_URL];
      return Reflect.apply(originalSend, this, [
        requestUrl == null ? body : rewriteMetaPixelRequestBodyForUrl(requestUrl, body),
      ]);
    };
  }

  installMetaPixelFormRequestSanitization();

  if (typeof HTMLImageElement !== "undefined") {
    installMetaPixelRequestUrlPropertySanitization(HTMLImageElement, "src");
  }
}

function rewriteFetchInput(input: RequestInfo | URL, rewriteUrl: UrlRewriter): RequestInfo | URL {
  if (typeof input === "string" || input instanceof URL) return rewriteUrl(input);
  if (typeof Request !== "undefined" && input instanceof Request) {
    const rewrittenUrl = rewriteUrl(input.url);
    return rewrittenUrl === input.url ? input : new Request(rewrittenUrl, input);
  }
  return input;
}

function installRequestSrcSanitization(
  elementConstructor: RequestSrcElementConstructor,
  rewriteUrl: UrlRewriter,
) {
  const srcDescriptor = Object.getOwnPropertyDescriptor(elementConstructor.prototype, "src");
  if (!srcDescriptor?.set) return;

  Object.defineProperty(elementConstructor.prototype, "src", {
    ...srcDescriptor,
    set(this: RequestSrcElement, value: string) {
      srcDescriptor.set?.call(this, rewriteUrl(value));
    },
  });
}

export function installAdsRequestSanitization({
  marker,
  rewriteUrl,
}: {
  marker: string;
  rewriteUrl: UrlRewriter;
}) {
  if (typeof window === "undefined") return;
  const sanitizationWindow = window as unknown as Record<string, unknown>;
  if (sanitizationWindow[marker]) return;
  sanitizationWindow[marker] = true;

  if (typeof window.fetch === "function") {
    const originalFetch = window.fetch.bind(window);
    window.fetch = (input, init) => originalFetch(rewriteFetchInput(input, rewriteUrl), init);
  }
  if (typeof navigator.sendBeacon === "function") {
    const originalSendBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = (url, data) => originalSendBeacon(rewriteUrl(url), data);
  }
  if (typeof XMLHttpRequest !== "undefined") {
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      return Reflect.apply(originalOpen, this, [method, rewriteUrl(url), ...rest]);
    } as XMLHttpRequest["open"];
  }
  if (typeof HTMLIFrameElement !== "undefined") {
    installRequestSrcSanitization(HTMLIFrameElement, rewriteUrl);
  }
  if (typeof HTMLImageElement !== "undefined") {
    installRequestSrcSanitization(HTMLImageElement, rewriteUrl);
  }
  if (typeof HTMLScriptElement !== "undefined") {
    installRequestSrcSanitization(HTMLScriptElement, rewriteUrl);
  }
}
